import React, { FC } from 'react';
import cx from 'classnames';

import 'app/assets/stylesheets/design_system/_divider.scss';

export type DividerSize = 8 | 16 | 24 | 32 | 48;

interface Props {
  /**
   * Allow custom styling via astroturf's `styled` function.
   *
   * @example
   *
   * ```
   * import styled from 'astroturf/react';
   * import Divider from 'js/design_system/Divider';
   *
   * const MyDivider = styled(Divider)`
   *   border-style: dashed;
   * `;
   * ```
   */
  className?: string;
  /**
   * The top and bottom margins around the divider.
   */
  size?: DividerSize;
}

/**
 * Component for dividers/horizontal rules, returns an `<hr>` tag
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import Divider from 'js/design_system/Divider';
 *
 * <Divider />
 *
 * // Size shorthand
 * <Divider.s16 />
 * ```
 *
 * ---
 * ## Custom Styling
 *
 * @example
 *
 * ```tsx
 * import styled from 'astroturf/react';
 * import { default as DividerBase } from 'js/design_system/Divider';
 *
 * const Divider = styled(DividerBase)`
 *   border-color: $ds-red;
 * `;
 *
 * <Divider />
 * ```
 */
const Divider: FC<Props> = ({ className, size = 24 }) => {
  switch (size) {
    case 8:
      return <hr className={cx('ds-divider-8', className)} />;
    case 16:
      return <hr className={cx('ds-divider-16', className)} />;
    case 24:
      return <hr className={cx('ds-divider-24', className)} />;
    case 32:
      return <hr className={cx('ds-divider-32', className)} />;
    case 48:
      return <hr className={cx('ds-divider-48', className)} />;
  }
};

type SubProps = Omit<Props, 'size'>;

// Not sure why this rule is triggering, these components
// do have display names, and they show up in the dev tools
/* eslint-disable react/display-name */
const sizes = {
  s8: (props: SubProps) => <Divider {...props} size={8} />,
  s16: (props: SubProps) => <Divider {...props} size={16} />,
  s24: (props: SubProps) => <Divider {...props} size={24} />,
  s32: (props: SubProps) => <Divider {...props} size={32} />,
  s48: (props: SubProps) => <Divider {...props} size={48} />,
};
/* eslint-enable react/display-name */

export default Object.assign(Divider, sizes);
